/**
 * Helpers config — module GSD à part (standalone workspace).
 */

/**
 * @param {'fr'|'en'} locale
 * @param {{ labelFr: string, labelEn: string }} tab
 */
export function resolveStandaloneModuleLabel(locale, tab) {
  return locale === "en" ? tab.labelEn : tab.labelFr;
}

/**
 * @param {string} basePath
 * @param {{ segment?: string, path?: string }} tab
 */
export function resolveStandaloneModuleTabPath(basePath, tab) {
  if (tab.path) return tab.path;
  const segment = tab.segment ?? "";
  return segment ? `${basePath.replace(/\/$/, "")}/${segment}` : basePath;
}

/**
 * @param {object} options
 * @param {string} options.id
 * @param {string} options.titleFr
 * @param {string} options.titleEn
 * @param {string} [options.subtitleFr]
 * @param {string} [options.subtitleEn]
 * @param {string} [options.icon]
 * @param {string} options.basePath
 * @param {Array<object>} options.tabs
 * @param {Array<{ from: string, segment?: string, to?: string }>} [options.legacyRoutes]
 * @param {string} [options.phase]
 */
export function createStandaloneModuleConfig({
  id,
  titleFr,
  titleEn,
  subtitleFr,
  subtitleEn,
  icon,
  basePath,
  tabs = [],
  legacyRoutes = [],
  phase = "phase1",
}) {
  const normalizedBase = basePath.replace(/\/$/, "");
  const normalizedTabs = tabs.map((tab, index) => ({
    id: tab.id ?? tab.testId ?? `tab-${index}`,
    segment: tab.segment ?? "",
    labelFr: tab.labelFr,
    labelEn: tab.labelEn,
    icon: tab.icon,
    status: tab.status ?? "active",
    testId: tab.testId ?? `gsd-standalone-tab-${tab.id ?? index}`,
    path: resolveStandaloneModuleTabPath(normalizedBase, tab),
  }));

  const legacy = legacyRoutes.map((route) => ({
    from: route.from,
    to: route.to ?? resolveStandaloneModuleTabPath(normalizedBase, route),
  }));

  return {
    id,
    title: { fr: titleFr, en: titleEn },
    subtitle: subtitleFr || subtitleEn ? { fr: subtitleFr ?? "", en: subtitleEn ?? "" } : null,
    icon,
    basePath: normalizedBase,
    tabs: normalizedTabs,
    sidebarItem: {
      id: `${id}-sidebar`,
      to: normalizedBase,
      labelFr: titleFr,
      labelEn: titleEn,
      icon,
      phase,
    },
    legacyRoutes: legacy,
  };
}

/**
 * Retire les anciennes routes sidebar et ajoute l'item module unique.
 * @param {Array<{ to: string }>} items
 * @param {string[]} legacyPaths
 * @param {{ id: string, to: string, labelFr: string, labelEn: string, icon?: string, phase?: string }} sidebarItem
 */
export function applyStandaloneModuleSidebar(items, legacyPaths, sidebarItem) {
  const filtered = items.filter((item) => !legacyPaths.includes(item.to));
  if (!filtered.some((item) => item.to === sidebarItem.to)) {
    filtered.push(sidebarItem);
  }
  return filtered;
}
